class OkazoOfferPicker {
  constructor() {
    this.dialog = null;
    this.trigger = null;
    this.restoreFocus = true;
    this.onClick = this.onClick.bind(this);
  }

  init() {
    document.addEventListener("click", this.onClick);
  }

  onClick(event) {
    const trigger = event.target?.closest?.("[data-okz-offer-picker-trigger]");
    if (!trigger) return;

    const template = trigger.closest("[data-okz-product-card]")?.querySelector("[data-okz-offer-picker-template]");
    if (!template || typeof HTMLDialogElement === "undefined") return;

    event.preventDefault();
    this.open(template, trigger);
  }

  open(template, trigger) {
    if (this.dialog) this.destroy(this.dialog);

    const dialog = document.createElement("dialog");
    dialog.className = "okz-dialog okz-offer-picker-dialog";
    dialog.innerHTML = template.innerHTML;
    this.prepareDialog(dialog);
    const title = dialog.querySelector("[data-okz-offer-picker-title]");
    if (title?.id) dialog.setAttribute("aria-labelledby", title.id);
    document.body.append(dialog);

    this.dialog = dialog;
    this.trigger = trigger;
    this.restoreFocus = true;
    this.bindDialog(dialog);

    try {
      dialog.showModal();
    } catch (_error) {
      dialog.remove();
      this.dialog = null;
      window.location.href = trigger.href;
      return;
    }

    document.documentElement.classList.add("okz-offer-picker-open");

    const selectedOption =
      dialog.querySelector('[data-okz-offer-option][aria-checked="true"]:not([aria-disabled="true"])') ||
      dialog.querySelector('[data-okz-offer-option]:not([aria-disabled="true"])');

    if (selectedOption) this.selectOption(dialog, selectedOption, false);

    window.requestAnimationFrame(() => {
      (selectedOption || dialog.querySelector("[data-okz-offer-picker-close]"))?.focus({ preventScroll: true });
    });
  }

  bindDialog(dialog) {
    dialog.addEventListener("click", (event) => {
      const closeButton = event.target.closest("[data-okz-offer-picker-close]");
      if (closeButton) {
        event.preventDefault();
        this.close();
        return;
      }

      const option = event.target.closest("[data-okz-offer-option]");
      if (option) {
        event.preventDefault();
        if (option.getAttribute("aria-disabled") === "true") return;
        const cameFromKeyboard = event.detail === 0;
        this.selectOption(dialog, option, cameFromKeyboard);
        if (!cameFromKeyboard && document.activeElement === option) option.blur();
        return;
      }

      if (event.target === dialog) this.close();
    });

    dialog.addEventListener("keydown", (event) => this.onKeydown(event, dialog));
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      this.close();
    });
    dialog.addEventListener("close", () => this.destroy(dialog));
    dialog.addEventListener("okazo:cart-added", () => this.close(false));
  }

  prepareDialog(dialog) {
    const picker = dialog.querySelector("[data-okz-offer-picker-content]");
    if (picker?.dataset.okzOfferPickerKind !== "condition") return;

    const optionsGroup = dialog.querySelector(".okz-offer-picker__options");
    const options = Array.from(optionsGroup?.querySelectorAll("[data-okz-offer-option]") || []);
    options
      .map((option, index) => ({
        option,
        index,
        rank: Number.parseInt(option.dataset.okzConditionRank || "99", 10),
      }))
      .sort((first, second) => first.rank - second.rank || first.index - second.index)
      .forEach(({ option }) => optionsGroup.append(option));
  }

  onKeydown(event, dialog) {
    const option = event.target.closest("[data-okz-offer-option]");
    if (!option) return;
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;

    event.preventDefault();

    const options = Array.from(dialog.querySelectorAll('[data-okz-offer-option]:not([aria-disabled="true"])'));
    const index = options.indexOf(option);
    if (index < 0 || options.length === 0) return;
    let nextIndex = index;

    if (["ArrowLeft", "ArrowUp"].includes(event.key)) nextIndex = index <= 0 ? options.length - 1 : index - 1;
    if (["ArrowRight", "ArrowDown"].includes(event.key)) nextIndex = index >= options.length - 1 ? 0 : index + 1;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = options.length - 1;

    this.selectOption(dialog, options[nextIndex], true);
  }

  selectOption(dialog, selectedOption, focus) {
    if (selectedOption.getAttribute("aria-disabled") === "true") return;

    const options = Array.from(dialog.querySelectorAll("[data-okz-offer-option]"));
    options.forEach((option) => {
      const isSelected = option === selectedOption;
      option.setAttribute("aria-checked", String(isSelected));
      option.tabIndex = isSelected && option.getAttribute("aria-disabled") !== "true" ? 0 : -1;
    });

    const form = dialog.querySelector("[data-okz-add-to-cart-form]");
    const input = form?.querySelector('[name="id"]');
    const price = dialog.querySelector("[data-okz-offer-picker-price]");
    const fullLink = dialog.querySelector("[data-okz-offer-picker-full-link]");
    const submitButton = form?.querySelector("[data-okz-add-to-cart-submit]");
    const variantId = selectedOption.dataset.okzVariantId;
    const variantLabel = selectedOption.dataset.okzVariantLabel;

    if (input && variantId) input.value = variantId;
    if (price && selectedOption.dataset.okzVariantPrice) price.textContent = selectedOption.dataset.okzVariantPrice;
    if (fullLink && selectedOption.dataset.okzVariantUrl) fullLink.href = selectedOption.dataset.okzVariantUrl;
    if (form && selectedOption.dataset.okzToastMessage)
      form.dataset.okzToastMessage = selectedOption.dataset.okzToastMessage;

    const facts = dialog.querySelector("[data-okz-offer-selected-facts]");
    const template = dialog.querySelector(`[data-okz-offer-facts-for="${variantId}"]`);
    if (facts && template) facts.replaceChildren(template.content.cloneNode(true));

    const conditionSummary = dialog.querySelector("[data-okz-offer-condition-summary]");
    const conditionSummaryText = dialog.querySelector("[data-okz-offer-condition-summary-text]");
    if (conditionSummary && conditionSummaryText) {
      const summary = selectedOption.dataset.okzConditionSummary || "";
      conditionSummaryText.textContent = summary;
      conditionSummary.hidden = summary.length === 0;
    }

    const image = dialog.querySelector(".okz-offer-picker__media img");
    if (image && selectedOption.dataset.okzVariantImage) {
      image.removeAttribute("srcset");
      image.src = selectedOption.dataset.okzVariantImage;
      image.alt = selectedOption.dataset.okzVariantImageAlt || "";
    }

    if (submitButton) {
      const baseLabel = submitButton.dataset.okzAddBaseLabel || submitButton.textContent.trim();
      const productTitle = submitButton.dataset.okzProductTitle || "";
      const ariaParts = [baseLabel, productTitle, variantLabel].filter(Boolean);
      submitButton.setAttribute("aria-label", ariaParts.join(" — "));
    }

    if (focus) selectedOption.focus({ preventScroll: true });
  }

  close(restoreFocus = true) {
    if (!this.dialog) return;

    const dialog = this.dialog;
    this.restoreFocus = restoreFocus;
    dialog.classList.add("is-closing");

    const closeDelay = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 170;
    window.setTimeout(() => {
      if (dialog.open) {
        dialog.close();
      } else {
        this.destroy(dialog);
      }
    }, closeDelay);
  }

  destroy(dialog) {
    if (!dialog || this.dialog !== dialog) return;

    const trigger = this.trigger;
    const shouldRestoreFocus = this.restoreFocus;

    document.documentElement.classList.remove("okz-offer-picker-open");
    dialog.remove();
    this.dialog = null;
    this.trigger = null;
    this.restoreFocus = true;

    if (shouldRestoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
  }
}

class OkazoConditionGuideDialog {
  constructor() {
    this.dialog = null;
    this.trigger = null;
    this.onClick = this.onClick.bind(this);
  }

  init() {
    document.addEventListener("click", this.onClick);
  }

  onClick(event) {
    const trigger = event.target?.closest?.("[data-okz-condition-guide-trigger]");
    if (trigger) {
      const guideKind = trigger.dataset.okzConditionGuideKind || "book";
      const dialog =
        document.querySelector(`[data-okz-condition-guide-dialog][data-okz-condition-guide-kind="${guideKind}"]`) ||
        document.querySelector("[data-okz-condition-guide-dialog]");
      if (!dialog || typeof HTMLDialogElement === "undefined" || !dialog.showModal) return;

      event.preventDefault();
      this.open(dialog, trigger);
      return;
    }

    const closeButton = event.target?.closest?.("[data-okz-condition-guide-close]");
    if (closeButton) {
      event.preventDefault();
      this.close(closeButton.closest("[data-okz-condition-guide-dialog]"));
      return;
    }

    const dialog = event.target?.closest?.("[data-okz-condition-guide-dialog]");
    if (dialog && event.target === dialog) this.close(dialog);
  }

  open(dialog, trigger) {
    if (this.dialog && this.dialog !== dialog) this.close(this.dialog, false);

    this.dialog = dialog;
    this.trigger = trigger;
    this.bindDialog(dialog);
    this.selectInitialCondition(dialog, trigger);
    dialog.classList.remove("is-closing");

    try {
      if (!dialog.open) dialog.showModal();
    } catch (_error) {
      this.dialog = null;
      this.trigger = null;
      window.location.href = trigger.href;
      return;
    }

    document.documentElement.classList.add("okz-condition-guide-open");
    window.requestAnimationFrame(() => {
      const closeButton = dialog.querySelector("[data-okz-condition-guide-close]");
      const selectedTab = dialog.querySelector('[data-okz-condition-dialog-tab][aria-selected="true"]');
      (closeButton || selectedTab)?.focus({ preventScroll: true });
    });
  }

  selectInitialCondition(dialog, trigger) {
    const selectedOffer = trigger
      ?.closest?.(".okz-offer-picker")
      ?.querySelector?.('[data-okz-offer-option][aria-checked="true"]');
    const selectedPicker = trigger
      ?.closest?.(".okazo-condition-picker")
      ?.querySelector?.(".product-form__input__radio:checked");
    const selectedKey = selectedOffer?.dataset.okzConditionKey || selectedPicker?.dataset.okzConditionKey;
    if (!selectedKey) return;

    const tab = dialog.querySelector(`[data-okz-condition-dialog-tab][data-okz-condition-key="${selectedKey}"]`);
    const tabs = tab?.closest?.("okazo-condition-tabs");
    if (tab && tabs?.select) tabs.select(tab);
  }

  bindDialog(dialog) {
    if (dialog.dataset.okazoConditionGuideBound === "true") return;
    dialog.dataset.okazoConditionGuideBound = "true";

    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      this.close(dialog);
    });

    dialog.addEventListener("close", () => this.destroy(dialog));
  }

  close(dialog = this.dialog, restoreFocus = true) {
    if (!dialog) return;

    dialog.dataset.okazoRestoreFocus = String(restoreFocus);
    dialog.classList.add("is-closing");

    const closeDelay = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 150;
    window.setTimeout(() => {
      if (dialog.open) {
        dialog.close();
      } else {
        this.destroy(dialog);
      }
    }, closeDelay);
  }

  destroy(dialog) {
    if (!dialog || this.dialog !== dialog) return;

    const trigger = this.trigger;
    const shouldRestoreFocus = dialog.dataset.okazoRestoreFocus !== "false";

    document.documentElement.classList.remove("okz-condition-guide-open");
    dialog.classList.remove("is-closing");
    dialog.removeAttribute("data-okazo-restore-focus");
    this.dialog = null;
    this.trigger = null;

    if (shouldRestoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
  }
}

new OkazoOfferPicker().init();
new OkazoConditionGuideDialog().init();
